// Figma: no frame provided yet — built using the established design system (bordered
// section shell, same as ../Esg/EsgSection.js) per the "extrapolate" decision.
// LOGIC PORTED from LC2's StakeSayTab.js (commons/components/HomeTabs/StakeSay/
// StakeSayTab.js): same `getReviews` query param derivation from route params
// (campaign_sfid > community_sfid > account_sfid), same `indexedReviews` bucketing by
// rating (0 = all) scoped to `dashboardCommunities` and gated on `isStakeAdmin ||
// review.visible_to_client`, same average-rating header and per-rating filter, same
// review list (see ./ReviewCard.js, ported from ReviewsTable/ReviewRow.js).
// SIMPLIFIED / NOT PORTED (both are stake-admin-only write flows, out of scope for this
// read-only pass):
//   - AddReviewModal (commons/components/HomeTabs/StakeSay/AddReviewModal.js) — the
//     "Add review" button + form.
//   - The per-review "Visible to client" toggle (see ./ReviewCard.js's header comment).
// The rating breakdown bars (LC2's ReviewsBars.js, built on a custom StyledBarRating) are
// rebuilt with a plain MUI LinearProgress instead of that custom styled component.
// Reviews are a feed, not tabular data, so this uses a plain card list + Previous/Next
// pager instead of ../components/DataTableShell.js (that shell is for columnar data).
import React, {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {Box, LinearProgress, Rating, Stack, Typography} from '@mui/material';

import {useGetReviewsQuery} from '../../../features/loyaltyCloudApi/reviewsSlice';
import {getIsStakeAdmin} from '../../../app/store/reducers/session';
import {getDashboardCommunities} from '../../../app/store/reducers/dashboard';
import {tokens} from '../../../themes/ThemeCustomization';
import ReviewCard from './ReviewCard';

const PAGE_SIZE = 5;
const RATING_FILTERS = [
    {label: 'All Reviews', value: 0},
    {label: '1 star', value: 1},
    {label: '2 stars', value: 2},
    {label: '3 stars', value: 3},
    {label: '4 stars', value: 4},
    {label: '5 stars', value: 5}
];

const StakeSaySection = () => {
    const params = useParams();
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const dashboardCommunities = useSelector(getDashboardCommunities);

    const reviewParam = useMemo(() => {
        if (params?.campaign_sfid) return {campaign_sfid: params.campaign_sfid};
        if (params?.community_sfid) return {property_sfid: params.community_sfid};
        if (params?.account_sfid) return {account_sfid: params.account_sfid};
        return null;
    }, [params]);

    const {data: reviews, isLoading, isFetching, isSuccess, isError} = useGetReviewsQuery(reviewParam, {skip: !reviewParam});

    const [filterValue, setFilterValue] = useState(0);
    const [page, setPage] = useState(0);

    const indexedReviews = useMemo(() => {
        const obj = {0: [], 1: [], 2: [], 3: [], 4: [], 5: []};
        if (isSuccess && reviews) {
            const communitySfids = dashboardCommunities.map((c) => c.sfid);
            reviews
                .filter((r) => communitySfids.includes(r.property_sfid))
                .forEach((review) => {
                    if (isStakeAdmin || review.visible_to_client) {
                        obj[review.rating]?.push(review);
                        obj[0].push(review);
                    }
                });
        }
        return obj;
    }, [reviews, isStakeAdmin, isSuccess, dashboardCommunities]);

    const displayReviews = indexedReviews[filterValue] || [];

    const averageRating = displayReviews.length > 0 ? displayReviews.reduce((acc, r) => acc + r.rating, 0) / displayReviews.length : 0;

    const bars = useMemo(
        () =>
            [1, 2, 3, 4, 5].map((value) => {
                const total = indexedReviews[0].length;
                const progress = total > 0 ? Math.round((indexedReviews[value].length / total) * 100) : 0;
                return {value, progress};
            }),
        [indexedReviews]
    );

    const handleSelect = (value) => {
        setFilterValue(value);
        setPage(0);
    };

    const pageCount = Math.max(1, Math.ceil(displayReviews.length / PAGE_SIZE));
    const clampedPage = Math.min(page, pageCount - 1);
    const pageReviews = displayReviews.slice(clampedPage * PAGE_SIZE, clampedPage * PAGE_SIZE + PAGE_SIZE);

    if (isLoading || isFetching) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 4}}>
                Loading reviews…
            </Typography>
        );
    }
    if (isError) {
        return (
            <Typography variant="body2" color="error.main" sx={{textAlign: 'center', py: 4}}>
                Failed to fetch reviews.
            </Typography>
        );
    }

    return (
        <Box sx={{border: `1px solid ${tokens.color.neutral200}`, borderRadius: '6px', backgroundColor: tokens.color.white, p: 2}}>
            <Stack spacing={2}>
                <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Resident feedback
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Rating value={averageRating} precision={0.1} readOnly size="small" />
                        <Typography variant="body2">{averageRating.toFixed(1)} out of 5</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {displayReviews.length === 1 ? '1 review' : `${displayReviews.length} reviews`}
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {RATING_FILTERS.map((filter) => (
                        <Box
                            key={filter.value}
                            component="button"
                            type="button"
                            disabled={indexedReviews[filter.value].length === 0}
                            onClick={() => handleSelect(filter.value)}
                            sx={{
                                border: `1px solid ${filterValue === filter.value ? tokens.color.sky600 : tokens.color.neutral200}`,
                                borderRadius: '999px',
                                backgroundColor: filterValue === filter.value ? tokens.color.sky50 : tokens.color.white,
                                px: 1.5,
                                py: 0.5,
                                cursor: indexedReviews[filter.value].length === 0 ? 'default' : 'pointer',
                                opacity: indexedReviews[filter.value].length === 0 ? 0.5 : 1
                            }}
                        >
                            <Typography variant="body2" fontWeight={500}>
                                {filter.label}
                            </Typography>
                        </Box>
                    ))}
                </Stack>

                <Stack direction="row" spacing={3}>
                    {bars.map((bar) => (
                        <Stack key={bar.value} spacing={0.5} alignItems="center" sx={{flex: 1}}>
                            <Typography variant="caption" color="text.secondary">
                                {bar.progress}%
                            </Typography>
                            <LinearProgress variant="determinate" value={bar.progress} sx={{width: '100%', height: 6, borderRadius: 3}} />
                            <Typography variant="caption" color="text.secondary">
                                {bar.value} star{bar.value > 1 ? 's' : ''}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>

                <Stack divider={<Box sx={{borderBottom: `1px solid ${tokens.color.neutral200}`}} />}>
                    {pageReviews.length > 0 ? (
                        pageReviews.map((review) => <ReviewCard key={review.id} review={review} />)
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 4}}>
                            Currently deploying StakeSay to residents. Reviews will be posted here when your Stakers respond.
                        </Typography>
                    )}
                </Stack>

                {displayReviews.length > PAGE_SIZE && (
                    <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                        <Box
                            component="button"
                            type="button"
                            disabled={clampedPage === 0}
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            sx={{
                                border: `1px solid ${tokens.color.neutral200}`,
                                borderRadius: '6px',
                                backgroundColor: tokens.color.white,
                                height: 32,
                                px: 1.5,
                                cursor: 'pointer',
                                opacity: clampedPage === 0 ? 0.5 : 1
                            }}
                        >
                            <Typography variant="body2" fontWeight={500}>
                                Previous
                            </Typography>
                        </Box>
                        <Box
                            component="button"
                            type="button"
                            disabled={clampedPage >= pageCount - 1}
                            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                            sx={{
                                border: `1px solid ${tokens.color.neutral200}`,
                                borderRadius: '6px',
                                backgroundColor: tokens.color.white,
                                height: 32,
                                px: 1.5,
                                cursor: 'pointer',
                                opacity: clampedPage >= pageCount - 1 ? 0.5 : 1
                            }}
                        >
                            <Typography variant="body2" fontWeight={500}>
                                Next
                            </Typography>
                        </Box>
                    </Stack>
                )}
            </Stack>
        </Box>
    );
};

export default StakeSaySection;
